import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { messageAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Send, Loader2, Lock, User, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime12h } from '../../utils/timeFormat';

/**
 * ClientChatThread (V413 · Client Messaging Audit — 2026-02-13)
 *
 * Renders a single message thread between a client and a talent for a
 * specific event/booking. Handles:
 *   • Prospective talent conversations (booking_id NULL, shift_id set).
 *   • Booked talent conversations (booking_id set).
 *   • Event-lifecycle read-only banner when the event has closed.
 *
 * Fixes the SweetWater / Claire bug — previously /client/messages/:threadId
 * silently rendered the messages LIST (no composer, no thread view).
 */
const POLL_INTERVAL_MS = 5000;

const ClientChatThread = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [locked, setLocked] = useState(false);
  const [lockReason, setLockReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState('');
  const endRef = useRef(null);
  const pollRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isClient = user.role === 'client' || user.role === 'admin' || user.role === 'super_admin';

  const load = useCallback(async () => {
    try {
      // Pull the thread meta (from the list) + messages in parallel.
      const [listRes, msgRes] = await Promise.all([
        api.get('/messages/threads').catch(() => ({ data: [] })),
        messageAPI.getMessages(threadId),
      ]);
      const th = (listRes.data || []).find((r) => r.id === threadId);
      setThread(th || null);
      setMessages(msgRes.data.messages || []);
      setLocked(!!msgRes.data.messaging_locked);
      setLockReason(msgRes.data.locked_reason || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!threadId) return undefined;
    pollRef.current = setInterval(() => {
      messageAPI.getMessages(threadId)
        .then((res) => {
          setMessages(res.data.messages || []);
          setLocked(!!res.data.messaging_locked);
          setLockReason(res.data.locked_reason || null);
        })
        .catch(() => { /* silent */ });
    }, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [threadId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = content.trim();
    if (!text || sending || locked) return;
    setSending(true);
    try {
      await messageAPI.sendMessage(threadId, { content: text });
      setContent('');
      // Optimistic — refetch to pick up the new row.
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 423) {
        setLocked(true);
        setLockReason(detail?.reason || 'event_closed');
        toast.error(detail?.message || 'This event chat is now read-only.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to send');
      }
    } finally {
      setSending(false);
    }
  };

  const lockCopy = () => {
    switch (lockReason) {
      case 'shift_archived': return 'This event has been archived — the conversation is read-only.';
      case 'shift_completed': return 'This event is marked complete — the conversation is read-only.';
      case 'shift_end_passed': return 'This event has ended — the conversation is read-only.';
      case 'admin_locked': return 'An admin has closed this event chat.';
      default: return 'This conversation is read-only because the event has ended.';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="client-chat-thread">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/client/messages')}
            data-testid="back-to-messages"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <p className="font-semibold text-gray-900 truncate" data-testid="thread-other-party-name">
                {thread?.other_party_name || 'Conversation'}
              </p>
              {locked && (
                <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-amber-100 text-amber-800 font-semibold flex items-center gap-1" data-testid="thread-locked-badge">
                  <Lock className="w-3 h-3" /> Closed
                </span>
              )}
            </div>
            {thread?.venue_name && (
              <p className="text-xs text-gray-500 truncate">{thread.venue_name}</p>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-4">
        {messages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No messages yet. Start the conversation below.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="thread-messages-list">
            {messages.map((m) => {
              const mine = m.sender_id === user.id;
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                    mine ? 'bg-cyan-500 text-white' : 'bg-white text-gray-900 border border-gray-200'
                  }`} data-testid={`thread-message-${m.id}`}>
                    {!mine && (
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                        {m.sender_name}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap break-words">{m.content}</p>
                    <p className={`text-[10px] mt-1 ${mine ? 'text-cyan-100' : 'text-gray-400'}`}>
                      {formatDateTime12h(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </main>

      <footer className="border-t border-gray-200 bg-white sticky bottom-0">
        <div className="max-w-3xl mx-auto px-4 py-3">
          {locked ? (
            <Alert className="border-amber-300 bg-amber-50" data-testid="thread-locked-banner">
              <Lock className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-sm text-amber-900">
                <strong>Event chat closed.</strong> {lockCopy()} The full history remains
                available above for your records.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex items-end gap-2">
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={isClient ? 'Message this talent…' : 'Message the client…'}
                rows={2}
                className="flex-1 resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                data-testid="thread-composer-input"
              />
              <Button
                onClick={send}
                disabled={sending || !content.trim()}
                className="bg-cyan-500 hover:bg-cyan-600 h-11"
                data-testid="thread-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default ClientChatThread;
